<template>
  <main class="public-voting-page">
    <div v-if="loading" class="public-voting-loading" aria-live="polite">
      <span class="public-voting-spinner" aria-hidden="true"></span>
      <span>Consultando resultados...</span>
    </div>

    <section v-else-if="error" class="public-voting-state public-voting-state--error">
      <div class="public-voting-state__icon" aria-hidden="true">!</div>
      <h1>No se pudieron cargar los resultados</h1>
      <p>El enlace es válido, pero ocurrió un error al consultar la publicación.</p>
      <button type="button" class="public-voting-button" @click="loadResults(true)">
        Reintentar
      </button>
    </section>

    <section v-else-if="notFound || !results" class="public-voting-state">
      <div class="public-voting-state__icon" aria-hidden="true">?</div>
      <h1>Resultados no disponibles</h1>
      <p>Verifica que el enlace sea correcto o que la publicación siga vigente.</p>
    </section>

    <template v-else-if="!results.metadata.resultsAvailable">
      <header class="public-voting-header">
        <div class="public-voting-header__icon" aria-hidden="true">&#9202;</div>
        <h1>Resultados aún no disponibles</h1>
        <p v-if="results.metadata.title">{{ results.metadata.title }}</p>
      </header>
      <section class="public-voting-card public-voting-card--centered">
        <p>
          La votación ya tiene un enlace público. El snapshot oficial aparecerá automáticamente
          cuando llegue la hora programada.
        </p>
        <p v-if="results.metadata.availableAt">
          Los resultados estarán disponibles el
          <strong>{{ formatDateTime(results.metadata.availableAt) }}</strong>.
        </p>
        <p v-else>La disponibilidad de resultados todavía no tiene una hora configurada.</p>
        <p class="public-voting-card__hint">
          Esta página se actualizará automáticamente cuando los resultados estén disponibles.
          <span v-if="polling"> Consultando...</span>
        </p>
      </section>
    </template>

    <template v-else>
      <header class="public-voting-header">
        <div class="public-voting-header__icon" aria-hidden="true">&#10003;</div>
        <h1>{{ results.metadata.title || "Resultados publicados" }}</h1>
        <p>{{ getVotingTypeLabel(results.metadata.typeKey) }}</p>
        <span class="public-voting-badge">Resultados oficiales</span>
      </header>

      <section class="public-voting-card public-voting-metrics">
        <div v-if="results.metadata.participationPercentage !== null">
          <span>Participación</span>
          <strong>{{ formatPercent(results.metadata.participationPercentage) }}</strong>
        </div>
        <div v-if="results.metadata.totalVotes !== null">
          <span>Votos emitidos</span>
          <strong>{{ formatNumber(results.metadata.totalVotes) }}</strong>
        </div>
        <div v-if="results.metadata.closesAt">
          <span>Cierre</span>
          <strong>{{ formatDate(results.metadata.closesAt) }}</strong>
        </div>
        <div>
          <span>Publicación</span>
          <strong>{{ formatDate(results.metadata.publishedAt) || "No disponible" }}</strong>
        </div>
      </section>

      <section class="public-voting-card public-voting-outcome">
        <header class="public-voting-card__header">
          <div>
            <h2>Resultados</h2>
            <p>Detalle del snapshot publicado.</p>
          </div>
          <span class="public-voting-count">
            {{ results.outcome.contests.length }}
            {{ results.outcome.contests.length === 1 ? "concurso" : "concursos" }}
          </span>
        </header>

        <div v-if="results.outcome.contests.length" class="public-voting-contests">
          <section
            v-for="contest in results.outcome.contests"
            :key="contest.contestKey"
            class="public-voting-contest"
          >
            <h3>{{ contest.title }}</h3>
            <div v-if="contest.options.length" class="public-voting-options">
              <article v-for="option in contest.options" :key="option.optionKey">
                <div class="public-voting-option-row">
                  <span>{{ option.label }}</span>
                  <strong>
                    {{ option.votes === null ? "-" : formatNumber(option.votes) }}
                    <small>
                      {{ option.percentage === null ? "-" : formatPercent(option.percentage) }}
                    </small>
                  </strong>
                </div>
                <div class="public-voting-progress">
                  <span :style="{ width: percentageWidth(option.percentage) }"></span>
                </div>
              </article>
            </div>
            <p v-else class="public-voting-muted">Este concurso no tiene opciones publicables.</p>
          </section>
        </div>
        <p v-else class="public-voting-muted">El snapshot no contiene concursos para mostrar.</p>
      </section>

      <section class="public-voting-verification">
        <div aria-hidden="true">&#128274;</div>
        <p>
          Esta publicación puede validarse mediante su huella de integridad.
          <router-link :to="verificationRoute">Verificar resultado</router-link>.
        </p>
      </section>
    </template>
  </main>
</template>

<script>
import { votingService } from "@/api/voting";
import { formatVotingDate, getVotingTypeLabel } from "@/share/voting";
import {
  formatVotingNumber,
  formatVotingPercent,
  normalizePublicResults,
} from "@/share/votingResults";

export default {
  name: "PublicVotingResults",
  data() {
    return {
      results: null,
      loading: true,
      error: false,
      notFound: false,
      polling: false,
      timer: null,
    };
  },
  computed: {
    verificationRoute() {
      return {
        name: "Public Voting Verification",
        params: { shareToken: this.$route.params.shareToken },
      };
    },
  },
  created() {
    this.loadResults(true);
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    getVotingTypeLabel,
    formatDate: formatVotingDate,
    formatDateTime: formatVotingDate,
    formatNumber: formatVotingNumber,
    formatPercent: formatVotingPercent,
    percentageWidth(value) {
      const percentage = Number(value);
      if (!Number.isFinite(percentage)) return "0%";
      return `${Math.min(100, Math.max(0, percentage))}%`;
    },
    async loadResults(initial) {
      if (initial) {
        this.loading = true;
        this.error = false;
        this.notFound = false;
      }
      try {
        const payload = await votingService.getPublicResults(
          this.$route.params.shareToken
        );
        this.results = normalizePublicResults(payload);
        if (this.results.metadata.resultsAvailable) this.stopPolling();
        else this.startPolling();
      } catch (error) {
        const status = error && error.response && error.response.status;
        if (status === 404) this.notFound = true;
        else this.error = true;
        if (initial) this.results = null;
        this.stopPolling();
      } finally {
        if (initial) this.loading = false;
      }
    },
    startPolling() {
      if (this.timer) return;
      this.polling = true;
      this.timer = setInterval(() => this.loadResults(false), 15000);
    },
    stopPolling() {
      this.polling = false;
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
  },
};
</script>

<style scoped>
.public-voting-page {
  min-height: 100vh;
  box-sizing: border-box;
  max-width: 980px;
  margin: 0 auto;
  padding: 42px 24px 64px;
  color: #1b273e;
  font-family: "Muli", Arial, sans-serif;
}

.public-voting-loading,
.public-voting-state {
  display: flex;
  min-height: 300px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
}

.public-voting-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #dfe4eb;
  border-top-color: #ffb500;
  border-radius: 50%;
  animation: public-voting-spin 700ms linear infinite;
}

.public-voting-state__icon,
.public-voting-header__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eef3fb;
  color: #1b273e;
  font-weight: 900;
}

.public-voting-state__icon {
  width: 64px;
  height: 64px;
  font-size: 28px;
}

.public-voting-state h1,
.public-voting-header h1 {
  margin: 12px 0 0;
  color: #1b273e;
  font-size: clamp(26px, 4vw, 38px);
  line-height: 1.15;
}

.public-voting-state p,
.public-voting-header p {
  margin: 0;
  color: #687386;
  font-size: 15px;
}

.public-voting-button {
  min-height: 44px;
  margin-top: 10px;
  border: 0;
  border-radius: 999px;
  padding: 10px 18px;
  background: #1b273e;
  color: #ffffff;
  font-weight: 800;
  cursor: pointer;
}

.public-voting-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  text-align: center;
}

.public-voting-header__icon {
  width: 68px;
  height: 68px;
  background: #1b273e;
  color: #ffffff;
  font-size: 28px;
}

.public-voting-badge,
.public-voting-count {
  display: inline-flex;
  border-radius: 999px;
  padding: 6px 10px;
  background: #e7f6ea;
  color: #237b37;
  font-size: 12px;
  font-weight: 800;
}

.public-voting-card {
  margin-top: 18px;
  border: 1px solid #dfe4eb;
  border-radius: 20px;
  padding: 22px;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(27, 39, 62, 0.06);
}

.public-voting-card--centered {
  max-width: 700px;
  margin-right: auto;
  margin-left: auto;
  text-align: center;
}

.public-voting-card--centered p {
  margin: 0;
  color: #687386;
  font-size: 15px;
  line-height: 1.65;
}

.public-voting-card--centered p + p {
  margin-top: 14px;
}

.public-voting-card__hint,
.public-voting-muted {
  color: #687386;
  font-size: 13px;
}

.public-voting-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.public-voting-metrics div {
  display: flex;
  min-height: 86px;
  flex-direction: column;
  justify-content: center;
  border-radius: 12px;
  padding: 12px;
  background: #f5f7fa;
  text-align: center;
}

.public-voting-metrics span {
  color: #687386;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.public-voting-metrics strong {
  margin-top: 5px;
  color: #1b273e;
  font-size: 22px;
}

.public-voting-metrics div:first-child strong {
  color: #a66f00;
}

.public-voting-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.public-voting-card__header h2 {
  margin: 0;
  font-size: 22px;
}

.public-voting-card__header p {
  margin: 5px 0 0;
  color: #687386;
  font-size: 14px;
}

.public-voting-count {
  background: #eef3fb;
  color: #31527d;
}

.public-voting-contests {
  display: grid;
  gap: 18px;
  margin-top: 20px;
}

.public-voting-contest {
  border: 1px solid #dfe4eb;
  border-radius: 14px;
  padding: 16px;
  background: #f9fafc;
}

.public-voting-contest h3 {
  margin: 0;
  font-size: 17px;
}

.public-voting-options {
  display: grid;
  gap: 16px;
  margin-top: 16px;
}

.public-voting-option-row {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  color: #1b273e;
  font-size: 14px;
}

.public-voting-option-row strong {
  text-align: right;
  white-space: nowrap;
}

.public-voting-option-row small {
  display: block;
  margin-top: 3px;
  color: #a66f00;
  font-size: 12px;
}

.public-voting-progress {
  height: 8px;
  overflow: hidden;
  margin-top: 8px;
  border-radius: 999px;
  background: #e5e9ef;
}

.public-voting-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #ffb500;
  transition: width 200ms ease;
}

.public-voting-verification {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 18px;
  color: #687386;
  font-size: 13px;
}

.public-voting-verification div {
  font-size: 20px;
}

.public-voting-verification p {
  margin: 0;
}

.public-voting-verification a {
  color: #1b273e;
  font-weight: 800;
}

@keyframes public-voting-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 720px) {
  .public-voting-page {
    padding: 28px 16px 48px;
  }

  .public-voting-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
